import { useLazyQuery, useMutation }               from '@apollo/client'
import { TrashIcon }                               from '@heroicons/react/20/solid'
import { ArrowUturnLeftIcon }                      from '@heroicons/react/24/solid'
import { useEffect }                               from 'react'
import { useDispatch, useSelector }                from 'react-redux'
import { useNavigate }                             from 'react-router-dom'
import { Button }                                  from '../../../components'
import { LoadingEllipsis }                         from '../../../components/Loaders'
import { setShopSizeGuides, setSizeGuidesRefetch } from '../../../features/shop/shopSlice'
import { setToasts, showToaster }                  from '../../../features/toaster/toasterSlice'
import { DELETE_SIZE_GUIDE, GET_SHOP_SIZE_GUIDES } from '../../../graphql/shop'
import MainLayout                                  from '../../../layouts/MainLayout'

export default function SettingsShopSizeGuidesList(): JSX.Element {
  const navigate: any              = useNavigate()
  const dispatch: any              = useDispatch()
  const currentUser: any           = useSelector( ( state: any ): any => state.currentUser.user )
  const shopSizeGuides: any        = useSelector( ( state: any ): any => state.shop.sizeGuides )
  const shopSizeGuidesRefetch: any = useSelector( ( state: any ): any => state.shop.sizeGuidesRefetch )

  const [ getSizeGuides, { loading, refetch } ] = useLazyQuery( GET_SHOP_SIZE_GUIDES, {
    notifyOnNetworkStatusChange: true,
    onCompleted:                 ( { shopSizeGuides }: any ): void => dispatch( setShopSizeGuides( shopSizeGuides ) )
  } )

  const [ deleteShopSizeGuide, { loading: deleting } ] = useMutation( DELETE_SIZE_GUIDE, {
    onCompleted: (): void => {
      dispatchMessage( 'success', 'Size Guide deleted!' )
      dispatch( setSizeGuidesRefetch( true ) )
    }
  } )

  const fetchSizeGuides: any = async (): Promise<void> => {
    await getSizeGuides()
  }

  const refetchSizeGuides: any = async (): Promise<void> => {
    await refetch()
    dispatch( setSizeGuidesRefetch( false ) )
  }

  const handleDelete: any = async ( id: string, url: string ): Promise<void> => {
    if ( window.confirm( 'Are you sure you want to delete this Guide Size?' ) ) {
      await deleteShopSizeGuide( { variables: { input: { id, url } } } )
    }
  }

  const dispatchMessage: any = ( type: string, message: string ): void => {
    dispatch( setToasts( [ { id: '1', type, message } ] ) )
    dispatch( showToaster( true ) )
  }

  useEffect( (): any => {
    let shouldFetch: boolean = true

    if ( shouldFetch && !shopSizeGuides?.length ) {
      fetchSizeGuides()
    }

    if ( shopSizeGuidesRefetch ) {
      refetchSizeGuides()
    }

    return (): void => {
      shouldFetch = false
    }
  }, [ shopSizeGuides ] )

  return (
    <MainLayout>
      <div className="max-w-screen-3xl mx-auto w-full">
        <div className="flex-grow lg:p-8 p-3 space-y-4 lg:space-y-8">
          { ( !currentUser || deleting ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4 mt-4 w-full" /> }
          { currentUser?.admin && !deleting &&
            <>
              <div className="flex justify-between w-full my-4">
                <div className="flex">
                  <Button
                    label="Back"
                    blue
                    onClick={ (): any => navigate( -1 ) }
                    icon={ <ArrowUturnLeftIcon className="w-4 h-4" /> }
                  />
                </div>
                <div className="flex space-x-4">
                  <Button label="Add new size guide" yellow onClick={ (): any => navigate( '/settings/shop/size-guides/add' ) } />
                </div>
              </div>

              { ( loading ) && <LoadingEllipsis klass="ellipse-yellow ellipse-xl p-4" /> }

              { !loading &&
                <>
                  { shopSizeGuides?.map( ( sizeGuide: any, index: any ): any =>
                                           <div className="flex items-center justify-between w-full bg-white rounded-3xl p-3 xl:p-4" key={ index }>
                                             <div className="flex items-center space-x-4">
                                               <a href={ `${ process.env.REACT_APP_CDN }/${ sizeGuide.url }` } target="_blank" className="flex items-center align-center w-48 h-24">
                                                 <img className="max-w-full max-h-full" src={ `${ process.env.REACT_APP_CDN }/${ sizeGuide.url }` } alt={ sizeGuide.name } />
                                               </a>
                                               <div>{ sizeGuide.name }</div>
                                             </div>

                                             <div className="flex items-center">
                                               <Button label="Edit" yellow onClick={ (): any => navigate( `/settings/shop/size-guides/edit/${ sizeGuide.id }` ) } />

                                               { currentUser.email === 'dawid@denfield.co.uk' &&
                                                 <button
                                                   className="flex items-center text-sm text-blue-input font-medium px-3 py-1 ml-2 bg-red-500 rounded-full"
                                                   onClick={ (): any => handleDelete( sizeGuide.id, sizeGuide.url ) }
                                                 >
                                                   <TrashIcon className="w-4 h-4" />
                                                   <span className="pl-1 text-left">Delete</span>
                                                 </button>
                                               }
                                             </div>
                                           </div> ) }
                </>
              }
            </>
          }
          { !currentUser?.admin &&
            <div className="row flex flex-col items-center w-full bg-white rounded-3xl p-3 xl:p-4">
              Access Denied!
            </div>
          }
        </div>
      </div>
    </MainLayout>
  )
}
